import api from "@/services/api";
import ICreateFidoCredentialResponse from "@/models/responses/ICreateFidoCredentialResponse.ts";
import IVerifyAssertionResponse from '@/models/responses/IVerifyAssertionResponse';

const jsonWebAuthnSupport = !!globalThis.PublicKeyCredential?.parseCreationOptionsFromJSON;

class PassKeyService {
  private pendingGetAbortController: AbortController | null = null;

  abortPendingGet() {
    this.pendingGetAbortController?.abort();
    this.pendingGetAbortController = null;
  }

  isSupported() {
    if (!jsonWebAuthnSupport) {
      return false;
    }
    return !!globalThis.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  }

  async createCredentialOptions(): Promise<PublicKeyCredentialCreationOptions> {
    const response = await api
      .post<PublicKeyCredentialCreationOptionsJSON>("v1.0/auth-fido/credential-options")
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
    return PublicKeyCredential.parseCreationOptionsFromJSON(response);
  }

  async createCredential(name: string, pkcOptions: PublicKeyCredentialCreationOptions) {
    const abortController = new AbortController();
    const options: CredentialCreationOptions = {
      publicKey: pkcOptions,
      signal: abortController.signal,
    };
    const fidoCredential = (await navigator.credentials.create(options)) as PublicKeyCredential;
    const response = await api
      .post<ICreateFidoCredentialResponse>(
        "v1.0/auth-fido/credential",
        {
          attestationResponse: fidoCredential.toJSON(),
          name: name,
        }
      )
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
    return response;
  }

  async createAssertionOptions(userIdentification?: string): Promise<PublicKeyCredentialRequestOptions> {
    const response = await api
      .post<PublicKeyCredentialRequestOptionsJSON>("v1.0/auth-fido/assertion-options", {
        userIdentification: userIdentification,
      })
      .then((response) => response.data)
      .catch((error) => {
        throw new Error(error);
      });
    return PublicKeyCredential.parseRequestOptionsFromJSON(response);
  }

  async isConditionalMediationAvailable() {
    return typeof PublicKeyCredential.isConditionalMediationAvailable === 'function' &&
      await PublicKeyCredential.isConditionalMediationAvailable();
  }

  async verifyAssertion(pkcOptions: PublicKeyCredentialRequestOptions, conditional = false) {
    // Any previous (typically conditional) get must be aborted so the new request can take the floor.
    this.pendingGetAbortController?.abort();
    const abortController = new AbortController();
    this.pendingGetAbortController = abortController;
    const options: CredentialRequestOptions = {
      mediation: conditional ? 'conditional' : undefined,
      publicKey: pkcOptions,
      signal: abortController.signal,
    };
    try {
      const assertionResponse = (await navigator.credentials.get(options)) as PublicKeyCredential;
      const response = await api
        .post<IVerifyAssertionResponse>("v1.0/auth-fido/assertion", assertionResponse.toJSON())
        .then((response) => response.data)
        .catch((error) => {
          throw new Error(error);
        });
      return response;
    } finally {
      if (this.pendingGetAbortController === abortController) {
        this.pendingGetAbortController = null;
      }
    }
  }
}

const passKeyService = new PassKeyService();
export { passKeyService };
